import { useRef, useState } from 'react';
import { FileTextIcon, FolderSimpleIcon, InfoIcon, UploadSimpleIcon } from '@phosphor-icons/react';

import Button from 'src/components/ui/Button';
import Tooltip from 'src/components/ui/Tooltip';

interface FileDropzoneProps {
  singleFile?: boolean;
  allowFolder?: boolean;
  disabled?: boolean;
  accept?: string;
  onFiles: (files: File[]) => void;
}

/** The dashed upload area from the batch upload modal. */
export default function FileDropzone({
  singleFile = false,
  allowFolder = false,
  disabled = false,
  accept,
  onFiles
}: FileDropzoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const addFiles = (files: File[]) => {
    if (disabled || files.length === 0) return;
    onFiles(files);
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple={!singleFile}
        accept={accept}
        className="hidden"
        disabled={disabled}
        onChange={(event) => {
          addFiles(Array.from(event.target.files ?? []));
          event.target.value = '';
        }}
      />
      {allowFolder && (
        <input
          ref={folderInputRef}
          type="file"
          multiple
          {...({ webkitdirectory: '' } as object)}
          className="hidden"
          disabled={disabled}
          onChange={(event) => {
            addFiles(Array.from(event.target.files ?? []));
            event.target.value = '';
          }}
        />
      )}

      <div
        className={[
          'flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors',
          disabled
            ? 'border-border-default bg-white opacity-50'
            : dragging
              ? 'border-blue-500 bg-blue-50'
              : 'border-border-default bg-white'
        ].join(' ')}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          const next = event.relatedTarget;
          if (next instanceof Node && event.currentTarget.contains(next)) return;
          setDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          addFiles(Array.from(event.dataTransfer.files));
        }}
      >
        <UploadSimpleIcon size={26} weight="light" className="text-content-default" />
        <span className="inline-flex items-center gap-1.5">
          <p className="text-base font-medium text-content-default">
            {dragging
              ? singleFile
                ? 'Drop to add file'
                : 'Drop to add files'
              : singleFile
                ? 'Drag file here to upload'
                : 'Drag files here to upload'}
          </p>
          <Tooltip title="Max 500 MB per file" placement="top">
            <button type="button" aria-label="Upload limits" className="inline-flex text-content-subtlest">
              <InfoIcon size={16} weight="light" />
            </button>
          </Tooltip>
        </span>
        <div className="mt-2 flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="md"
            icon={FileTextIcon}
            disabled={disabled}
            onClick={() => fileInputRef.current?.click()}
            text={singleFile ? 'Select File' : 'Select Files'}
          />
          {allowFolder && (
            <Button
              type="button"
              variant="outline"
              size="md"
              icon={FolderSimpleIcon}
              disabled={disabled}
              onClick={() => folderInputRef.current?.click()}
              text="Select Folder"
            />
          )}
        </div>
      </div>
    </>
  );
}
